import { useEffect, useState, MutableRefObject } from 'react';
import { ScrollSource } from './internal/scroll-event-bus';

/**
 * Scoped Element Search
 * 
 * WHY: Prevents finding wrong elements when duplicate IDs exist.
 * 
 * PROBLEM: When a modal is open, both the background page and modal render
 * the same address content with identical element IDs (e.g., "desktop-overview").
 * 
 * SOLUTION: Scope the search based on source:
 * - Modal: Search within [data-scroll-scope="modal"] container
 * - Window: Search globally with document.getElementById
 * 
 * This ensures IntersectionObserver tracks the correct elements.
 */
function getScopedElement(elementId: string, source: ScrollSource): HTMLElement | null {
  if (source === 'modal') {
    const modalContainer = document.querySelector('[data-scroll-scope="modal"]');
    return modalContainer?.querySelector(`#${elementId}`) as HTMLElement | null;
  }
  return document.getElementById(elementId);
}

/**
 * Active Tab Tracking Hook
 * 
 * WHY: Automatically highlights the tab for the currently visible section.
 * 
 * HOW: Uses IntersectionObserver to detect which sections are in view.
 * 
 * KEY DETAILS:
 * - Observer root is scoped to source (modal container or null for window)
 * - Ignores visibility changes during programmatic scrolling (isScrollingRef)
 * - Updates active tab to the first visible section
 * - rootMargin accounts for sticky header + tab bar height
 * 
 * This provides visual feedback as users scroll through the page.
 */
export function useActiveTabTracking(
  tabs: Array<{ id: string }>,
  viewport: 'desktop' | 'mobile',
  stickyTopOffset: number,
  tabBarHeight: number,
  isScrollingRef: MutableRefObject<boolean>,
  source: ScrollSource
) {
  const [activeTab, setActiveTab] = useState(tabs[0]?.id ?? '');

  useEffect(() => {
    if (tabs.length === 0) return;

    // Track intersection ratios for each section to determine most visible
    const sectionVisibility = new Map<string, number>();
    
    // Get the scroll container based on source
    const scrollContainer = source === 'modal' 
      ? document.querySelector('[data-scroll-scope="modal"]') as HTMLElement | null
      : null;

    const observer = new IntersectionObserver(
      entries => {
        if (isScrollingRef.current) return;
        
        entries.forEach(entry => {
          const tabId = entry.target.id.replace(`${viewport}-`, '');
          if (entry.isIntersecting) {
            sectionVisibility.set(tabId, entry.intersectionRatio);
          } else {
            sectionVisibility.delete(tabId);
          }
        });

        // Find the most visible section (highest intersection ratio)
        // If ratios are equal, prefer the last one in tab order (section further down the page)
        let mostVisibleTab: string | null = null;
        let highestRatio = 0;
        
        tabs.forEach(tab => {
          const ratio = sectionVisibility.get(tab.id);
          if (ratio !== undefined && ratio >= highestRatio) {
            highestRatio = ratio;
            mostVisibleTab = tab.id;
          }
        });

        if (mostVisibleTab) {
          setActiveTab(mostVisibleTab);
        }
      },
      {
        root: scrollContainer,
        rootMargin: `-${stickyTopOffset + tabBarHeight}px 0px -40% 0px`,
        threshold: [0, 0.25, 0.5, 0.75, 1.0],
      }
    );
      
    tabs.forEach(tab => {
      const elementId = `${viewport}-${tab.id}`;
      const el = getScopedElement(elementId, source);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [tabs, viewport, stickyTopOffset, tabBarHeight, source, isScrollingRef]);

  return { activeTab, setActiveTab };
}
